import { useEffect, useMemo, useState } from "react";
import { getClienteById } from "../../../services/usuarios_tienda/clientes";
import { getDireccionesByCliente } from "../../../services/usuarios_tienda/clientes_direccion";
import "./CustomerViewModal.css";

const formatDate = (dt) => (dt ? String(dt).split("T")[0] : "—");

const formatPhone = (code, phone) => {
  const joined = `${String(code || "").trim()} ${String(phone || "").trim()}`.trim();
  return joined || "—";
};

const buildAddressLines = (d) => {
  const lines = [
    d?.direccion_linea_1,
    d?.direccion_linea_2,
    d?.referencia,
    d?.codigo_postal ? `CP ${d.codigo_postal}` : null,
  ].filter(Boolean);

  return lines.length ? lines : ["—"];
};

function CustomerViewModal({ clienteId, preview, onClose }) {
  const [cliente, setCliente] = useState(null);
  const [direcciones, setDirecciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!clienteId) return;

    let alive = true;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKeyDown);

    (async () => {
      setLoading(true);
      setError(null);
      setCliente(null);
      setDirecciones([]);

      try {
        const [clienteData, direccionesData] = await Promise.all([
          getClienteById(clienteId),
          getDireccionesByCliente(clienteId),
        ]);

        if (!alive) return;
        setCliente(clienteData || null);
        setDirecciones(Array.isArray(direccionesData) ? direccionesData : []);
      } catch (e) {
        console.error("Error cargando cliente:", e);
        if (!alive) return;
        setError(e?.message || "No se pudo cargar la información del cliente.");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [clienteId, onClose]);

  const displayName = useMemo(() => {
    const fromApi = `${cliente?.nombre || ""} ${cliente?.apellido || ""}`.trim();
    if (fromApi) return fromApi;
    return preview?.cliente_nombre || "Cliente";
  }, [cliente?.nombre, cliente?.apellido, preview?.cliente_nombre]);

  if (!clienteId) return null;

  const closeOverlay = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    <div className="cvx-overlay" onMouseDown={closeOverlay}>
      <div className="cvx-modal" onMouseDown={(e) => e.stopPropagation()}>
        <header className="cvx-header">
          <div className="cvx-title">
            <h2 className="cvx-h1">{displayName}</h2>
            <p className="cvx-sub">Cliente #{clienteId}</p>
          </div>
          <button
            type="button"
            className="cvx-close"
            onClick={onClose}
            aria-label="Cerrar"
          >
            ×
          </button>
        </header>

        {error && (
          <div className="cvx-alert" role="alert">
            {error}
          </div>
        )}

        <div className="cvx-body">
          <section className="cvx-panel cvx-panel--profile">
            <h3 className="cvx-panel__title">Datos del cliente</h3>

            {loading ? (
              <div className="cvx-loading">Cargando datos...</div>
            ) : (
              <dl className="cvx-kv">
                <div className="cvx-kv__row">
                  <dt>ID</dt>
                  <dd>{cliente?.cliente_id ?? clienteId}</dd>
                </div>
                <div className="cvx-kv__row">
                  <dt>Nombre</dt>
                  <dd>{cliente?.nombre || "—"}</dd>
                </div>
                <div className="cvx-kv__row">
                  <dt>Apellido</dt>
                  <dd>{cliente?.apellido || "—"}</dd>
                </div>
                <div className="cvx-kv__row">
                  <dt>Correo</dt>
                  <dd>{cliente?.correo || preview?.correo || "—"}</dd>
                </div>
                <div className="cvx-kv__row">
                  <dt>Teléfono</dt>
                  <dd>
                    {formatPhone(cliente?.codigo_telefono, cliente?.telefono)}
                  </dd>
                </div>
                <div className="cvx-kv__row">
                  <dt>Registrado</dt>
                  <dd>{formatDate(cliente?.fecha_creado)}</dd>
                </div>
              </dl>
            )}
          </section>

          <section className="cvx-panel cvx-panel--addresses">
            <h3 className="cvx-panel__title">Direcciones registradas</h3>

            {loading ? (
              <div className="cvx-loading">Cargando direcciones...</div>
            ) : direcciones.length === 0 ? (
              <div className="cvx-empty">Este cliente no tiene direcciones registradas.</div>
            ) : (
              <div className="cvx-address-list">
                {direcciones.map((d) => (
                  <article
                    key={d.cliente_direccion_id}
                    className="cvx-address-card"
                  >
                    <div className="cvx-address-card__head">
                      <span className="cvx-address-card__name">
                        {d?.nombre_destinatario || "Sin destinatario"}
                      </span>
                      {Number(d.direccion_principal) ? (
                        <span className="cvx-badge">Principal</span>
                      ) : null}
                    </div>
                    <div className="cvx-address-card__body">
                      {buildAddressLines(d).map((line, idx) => (
                        <p key={idx}>{line}</p>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        </div>

        <footer className="cvx-footer">
          <button type="button" className="cvx-btn" onClick={onClose}>
            Cerrar
          </button>
        </footer>
      </div>
    </div>
  );
}

export default CustomerViewModal;
